import React, { useState } from 'react';
import colors from './colors'; // Importe o arquivo de cores
import { InputMask } from 'primereact/inputmask';
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline"; // Ícones de olho do Heroicons

const Input = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  color = 'primary',
  fullWidth = false,
  options = [], // Recebe as opções caso o tipo seja 'select'
  mask, // A máscara que será aplicada
  rows = 3,
  ...props
}) => {

  const [showPassword, setShowPassword] = useState(false); // Estado para exibir ou ocultar a senha

  // Estilos base para o input/select
  const baseClasses = 'py-2 px-4 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-opacity-50';

  // Definindo cores dinamicamente com base no arquivo colors.js
  const colorClasses = {
    primary: `border-[${colors.primary}] placeholder-${colors.primary} text-${colors.primary}`,
    secondary: `border-[${colors.secondary}] placeholder-${colors.secondary} text-${colors.secondary}`,
    error: `border-[${colors.error}] placeholder-${colors.error} text-${colors.error}`,
    success: `border-[${colors.success}] placeholder-${colors.success} text-${colors.success}`,
    warning: `border-[${colors.warning}] placeholder-${colors.warning} text-${colors.warning}`,
    light: `border-[${colors.light}] placeholder-${colors.light} text-black`,
  };

  // Aplicando as classes de cor ao input/select
  const inputClasses = `${baseClasses} ${colorClasses[color]} border-2 ${fullWidth ? 'w-full' : ''}`;

  // Alternar visibilidade da senha
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Função para determinar a máscara com base no tipo
  const getMask = () => {
    if (mask) return mask; // Máscara customizada passada como prop

    // Máscaras padrões para CPF, CNPJ, celular e CEP
    if (type === 'cpf') return { value: '999.999.999-99', slot: 'XXX.XXX.XXX-XX' }; // CPF
    if (type === 'cnpj') return { value: '99.999.999/9999-99', slot: 'XX.XXX.XXX/XXXX-XX' }; // CNPJ
    if (type === 'cep') return { value: '99999-999', slot: 'XXXXX-XXX' }; // CEP
    if (type === 'celular') return { value: '(99) 99999-9999', slot: '(XX) XXXXX-XXXX' }; // Celular
    return { value: '', slot: '' }; // Sem máscara padrão
  };

  // Obtendo a máscara e o slot para cada tipo
  const maskProps = getMask();

  // Verificando se mask foi passada diretamente no prop
  const appliedMask = mask || maskProps.value;
  const appliedSlotChar = maskProps.slot;

  return (
    <div className={`${fullWidth ? 'w-full' : ''}`}>
      {/* Label */}
      {label && <label htmlFor={label} className="block text-sm font-medium text-gray-700 mb-2">{label}</label>}

      {/* Renderiza o campo conforme o tipo */}
      {type === 'select' ? (
        <div className="relative">
          <select
            id={label}
            value={value}
            onChange={onChange}
            className={`${inputClasses} appearance-none pr-10`} // Adicionando padding à direita para a seta
            {...props}
          >
            {options.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Seta personalizada */}
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">
            ▼
          </span>
        </div>
      ) : type === 'textarea' ? (
        <textarea
          id={label}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
          className={inputClasses}
          style={{ resize: 'none' }}
          {...props}
        />
      ) : (
        // Caso haja uma máscara, utiliza InputMask, senão usa o input padrão
        appliedMask ? (
          <InputMask
            value={value}
            onChange={onChange}
            mask={appliedMask} // Passando a máscara aplicada
            placeholder={placeholder}
            slotChar={appliedSlotChar} // Passando o slot da máscara
            className={inputClasses}
            {...props} // Permite adicionar outras props personalizadas
          />
        ) : (
          <div className="relative">
            <input
              type={type === "password" ? (showPassword ? "text" : "password") : type}
              id={label}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              className={inputClasses}
              {...props} // Permite adicionar outras props personalizadas
            />
            {/* Ícone de olho para campos do tipo "password" */}
            {type === "password" && (
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default Input;
